import { ChevronLeft, ChevronRight } from 'lucide-react';

import { t } from '../../../lib/i18n';
import { type AuctionScheduleItem } from '../types';
import { UKRAINE_TIMEZONE, cn, nowInUkraine, toDateKey } from '../utils';

const WEEKDAYS = ['пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'нд'];
const MONTH_FMT = new Intl.DateTimeFormat('uk-UA', { month: 'long', year: 'numeric' });

interface Props {
  viewMonth: Date;
  selectedDate: Date;
  auctionsByDate: Record<string, AuctionScheduleItem[]>;
  onSelectDate: (date: Date) => void;
  onPrevMonth: () => void;
  onNextMonth: () => void;
}

/** Dot ink for a day: ending within 24h is the alarm, everything else is calm. */
function indicatorColor(dayAuctions: AuctionScheduleItem[]): string {
  const now = nowInUkraine();
  const endingSoon = dayAuctions.some((auction) => {
    const end = new Date(
      new Date(auction.end_date).toLocaleString('en-US', { timeZone: UKRAINE_TIMEZONE }),
    );
    const hours = (end.getTime() - now.getTime()) / 3_600_000;
    return hours <= 24 && hours > 0;
  });
  if (endingSoon) return 'bg-cf-danger';
  if (dayAuctions.some((auction) => auction.status === 'active')) return 'bg-cf-accent';
  return 'bg-cf-past';
}

/** Month calendar with closing dots — Design D tokens, Monday-first like the UI locale. */
export function MonthGrid({
  viewMonth,
  selectedDate,
  auctionsByDate,
  onSelectDate,
  onPrevMonth,
  onNextMonth,
}: Props) {
  const year = viewMonth.getFullYear();
  const month = viewMonth.getMonth();
  // getDay() is Sunday-first; the Ukrainian week starts on Monday.
  const firstWeekday = (new Date(year, month, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const selectedKey = toDateKey(selectedDate);
  const cells: Array<Date | null> = [
    ...Array.from({ length: firstWeekday }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => new Date(year, month, i + 1)),
  ];

  return (
    <div>
      <div className="mb-2 flex items-center justify-between px-1">
        <button
          type="button"
          onClick={onPrevMonth}
          aria-label={t('auctionSchedule.calendar.previousMonth')}
          className="flex h-8 w-8 items-center justify-center rounded-cf-md text-cf-ink-muted hover:bg-cf-fill"
        >
          <ChevronLeft size={18} strokeWidth={1.75} />
        </button>
        <span className="text-sm font-semibold">{MONTH_FMT.format(viewMonth)}</span>
        <button
          type="button"
          onClick={onNextMonth}
          aria-label={t('auctionSchedule.calendar.nextMonth')}
          className="flex h-8 w-8 items-center justify-center rounded-cf-md text-cf-ink-muted hover:bg-cf-fill"
        >
          <ChevronRight size={18} strokeWidth={1.75} />
        </button>
      </div>

      <div className="grid grid-cols-7 text-center font-mono text-[10px] tracking-[0.12em] text-cf-ink-subtle uppercase">
        {WEEKDAYS.map((day) => (
          <div key={day} className="py-1.5">
            {day}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7">
        {cells.map((date, index) => {
          if (!date) return <div key={`empty-${index}`} />;
          const key = toDateKey(date);
          const dayAuctions = auctionsByDate[key] || [];
          const isSelected = key === selectedKey;
          return (
            <button
              key={key}
              type="button"
              onClick={() => onSelectDate(date)}
              className={cn(
                'relative mx-auto flex h-9 w-9 items-center justify-center rounded-cf-md font-mono text-[13px]',
                isSelected ? 'bg-cf-brand text-white' : 'hover:bg-cf-fill',
              )}
            >
              {date.getDate()}
              {dayAuctions.length > 0 && (
                <span className="absolute bottom-1 left-1/2 flex -translate-x-1/2 gap-0.5">
                  <span className={cn('h-1 w-1 rounded-cf-xs', indicatorColor(dayAuctions))} />
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
